import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLSemanticMastery = () => {
    const practicalExampleCode = `<article>
  <header>
    <h2>Article Title</h2>
  </header>
  <p>Content goes here</p>
</article>`;

    const footerExampleCode = `            <!-- Article footer with metadata --&gt;
            <footer>
                <section aria-labelledby="tags-heading">
                    <h3 id="tags-heading">Tags</h3>
                    <ul>
                        <li><a href="/tag/html" rel="tag">HTML</a></li>
                        <li><a href="/tag/semantic" rel="tag">Semantic HTML</a></li>
                        <li><a href="/tag/accessibility" rel="tag">Accessibility</a></li>
                        <li><a href="/tag/seo" rel="tag">SEO</a></li>
                    </ul>
                </section>

                <section aria-labelledby="share-heading">
                    <h3 id="share-heading">Share this article</h3>
                    <ul class="share-buttons">
                        <li><a href="https://twitter.com/share?url=..." rel="noopener">Twitter</a></li>
                        <li><a href="https://facebook.com/sharer?u=..." rel="noopener">Facebook</a></li>
                        <li><a href="https://linkedin.com/shareArticle?url=..." rel="noopener">LinkedIn</a></li>
                    </ul>
                </section>
            </footer>
        </article>`;

    const completeSemanticPageCode = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="Complete guide to semantic HTML5 structure and best practices">
    <title>Mastering Semantic HTML | Web Development Blog</title>
    <link rel="canonical" href="https://example.com/semantic-html">
</head>
<body>
    <!-- Skip navigation for accessibility --&gt;
    <a href="#main-content" class="skip-link">Skip to main content</a>

    <!-- Site header with branding and navigation --&gt;
    <header role="banner">
        <div class="site-branding">
            <img src="logo.svg" alt="Web Dev Blog Logo" width="150" height="50">
            <p class="site-tagline">Learning web development, one article at a time</p>
        </div>

        <nav aria-label="Main navigation">
            <ul>
                <li><a href="/" aria-current="page">Home</a></li>
                <li><a href="/articles">Articles</a></li>
                <li><a href="/tutorials">Tutorials</a></li>
                <li><a href="/about">About</a></li>
                <li><a href="/contact">Contact</a></li>
            </ul>
        </nav>

        <form role="search" class="search-form">
            <label for="search">Search articles:</label>
            <input type="search" id="search" name="q" placeholder="Search...">
            <button type="submit">Search</button>
        </form>
    </header>

    <!-- Main content area --&gt;
    <main id="main-content" role="main">
        <!-- Breadcrumb navigation --&gt;
        <nav aria-label="Breadcrumb">
            <ol>
                <li><a href="/">Home</a></li>
                <li><a href="/articles">Articles</a></li>
                <li><a href="/articles/html">HTML</a></li>
                <li aria-current="page">Mastering Semantic HTML</li>
            </ol>
        </nav>

        <!-- Primary article --&gt;
        <article itemscope itemtype="https://schema.org/BlogPosting">
            <!-- Article header with metadata --&gt;
            <header>
                <h1 itemprop="headline">Mastering Semantic HTML: A Complete Guide</h1>
                
                <div class="article-meta">
                    <address>
                        By <a href="/author/jane-doe" rel="author" itemprop="author">Jane Doe</a>
                    </address>
                    
                    <time datetime="2025-01-15T09:00:00Z" itemprop="datePublished">
                        Published: January 15, 2025
                    </time>
                    
                    <time datetime="2025-01-20T14:30:00Z" itemprop="dateModified">
                        Updated: January 20, 2025
                    </time>
                    
                    <span>Reading time: <data value="12">12 minutes</data></span>
                </div>

                <figure>
                    <img src="semantic-html-cover.jpg" 
                         alt="Diagram showing HTML5 semantic elements" 
                         width="1200" 
                         height="630"
                         itemprop="image">
                    <figcaption>HTML5 semantic structure overview</figcaption>
                </figure>
            </header>

            <!-- Article content --&gt;
            <div itemprop="articleBody">
                <!-- Introduction section --&gt;
                <section aria-labelledby="intro-heading">
                    <h2 id="intro-heading">Introduction</h2>
                    <p>
                        Semantic HTML is the foundation of modern web development. 
                        In this comprehensive guide, we'll explore how to structure 
                        your pages for maximum <strong>accessibility</strong>, 
                        <strong>SEO</strong>, and <strong>maintainability</strong>.
                    </p>
                </section>

                <!-- Main content sections --&gt;
                <section aria-labelledby="what-is-semantic">
                    <h2 id="what-is-semantic">What is Semantic HTML?</h2>
                    <p>
                        Semantic HTML means using HTML elements that clearly describe 
                        their meaning and purpose...
                    </p>

                    <blockquote cite="https://www.w3.org/standards/">
                        <p>
                            "The Web is designed to work for all people, whatever their 
                            hardware, software, language, location, or ability."
                        </p>
                        <footer>
                             <cite>W3C Web Accessibility Initiative</cite>
                        </footer>
                    </blockquote>
                </section>

                <section aria-labelledby="why-it-matters">
                    <h2 id="why-it-matters">Why Semantic HTML Matters</h2>
                    
                    <h3>1. Accessibility Benefits</h3>
                    <p>Screen readers use semantic elements to help users navigate...</p>

                    <h3>2. SEO Advantages</h3>
                    <p>Search engines give more weight to semantic elements...</p>

                    <h3>3. Code Maintainability</h3>
                    <p>Developers can understand structure at a glance...</p>
                </section>

                <section aria-labelledby="practical-example">
                    <h2 id="practical-example">Practical Example</h2>
                    
                    <figure>
                        ${practicalExampleCode}
                        <figcaption>Example of semantic article structure</figcaption>
                    </figure>
                </section>

                <section aria-labelledby="takeaways">
                    <h2 id="takeaways">Key Takeaways</h2>
                    <ul>
                        <li>Always use semantic elements over generic <code>&lt;div&gt;</code></li>
                        <li>Maintain proper heading hierarchy (H1  H2  H3)</li>
                        <li>Include only one <code>&lt;main&gt;</code> element per page</li>
                        <li>Add ARIA labels when using multiple <code>&lt;nav&gt;</code> elements</li>
                        <li>Test with screen readers to verify accessibility</li>
                    </ul>
                </section>
            </div>

${footerExampleCode}

        <!-- Related articles section --&gt;
        <section aria-labelledby="related-heading">
            <h2 id="related-heading">Related Articles</h2>
            <div class="article-grid">
                <article>
                    <header>
                        <h3><a href="/article-2">Understanding HTML5 Forms</a></h3>
                        <p class="excerpt">Learn advanced form techniques...</p>
                    </header>
                </article>

                <article>
                    <header>
                        <h3><a href="/article-3">Accessibility Best Practices</a></h3>
                        <p class="excerpt">Make your website accessible to all...</p>
                    </header>
                </article>
            </div>
        </section>

        <!-- Comments section --&gt;
        <section aria-labelledby="comments-heading">
            <h2 id="comments-heading">Comments (<data value="5">5</data>)</h2>

            <article class="comment">
                <header>
                    <h3>Re: Mastering Semantic HTML</h3>
                    <p>
                        By <strong>John Smith</strong> on
                        <time datetime="2025-01-16T10:30:00Z">January 16, 2025</time>
                    </p>
                </header>
                <p>Great article! Very helpful explanation of semantic elements.</p>
            </article>

            <!-- Add comment form --&gt;
            <section aria-labelledby="add-comment">
                <h3 id="add-comment">Leave a Comment</h3>
                <form method="post" action="/comments">
                    <div>
                        <label for="name">Name:</label>
                        <input type="text" id="name" name="name" required>
                    </div>
                    <div>
                        <label for="comment">Comment:</label>
                        <textarea id="comment" name="comment" rows="5" required></textarea>
                    </div>
                    <button type="submit">Post Comment</button>
                </form>
            </section>
        </section>
    </main>

    <!-- Sidebar with complementary content --&gt;
    <aside role="complementary" aria-labelledby="sidebar-heading">
        <h2 id="sidebar-heading" class="sr-only">Sidebar</h2>

        <section aria-labelledby="about-author">
            <h3 id="about-author">About the Author</h3>
            <figure>
                <img src="jane-doe.jpg" alt="Jane Doe" width="100" height="100">
                <figcaption>
                    <strong>Jane Doe</strong> is a senior web developer with 10 years of experience.
                </figcaption>
            </figure>
            <p><a href="/author/jane-doe">View all articles by Jane</a></p>
        </section>

        <section aria-labelledby="newsletter">
            <h3 id="newsletter">Newsletter</h3>
            <p>Get weekly web development tips delivered to your inbox.</p>
            <form method="post" action="/newsletter">
                <label for="email">Email:</label>
                <input type="email" id="email" name="email" required>
                <button type="submit">Subscribe</button>
            </form>
        </section>

        <section aria-labelledby="popular">
            <h3 id="popular">Popular Articles</h3>
            <ol>
                <li><a href="/popular-1">CSS Grid Layout Guide</a></li>
                <li><a href="/popular-2">JavaScript ES6 Features</a></li>
                <li><a href="/popular-3">Responsive Design Patterns</a></li>
            </ol>
        </section>
    </aside>

    <!-- Site footer --&gt;
    <footer role="contentinfo">
        <div class="footer-content">
            <section aria-labelledby="footer-about">
                <h3 id="footer-about">About Web Dev Blog</h3>
                <p>
                    We're passionate about teaching modern web development.
                    Join our community of learners and build amazing websites.
                </p>
            </section>

            <nav aria-label="Footer navigation">
                <h3>Quick Links</h3>
                <ul>
                    <li><a href="/privacy">Privacy Policy</a></li>
                    <li><a href="/terms">Terms of Service</a></li>
                    <li><a href="/sitemap">Sitemap</a></li>
                    <li><a href="/rss">RSS Feed</a></li>
                </ul>
            </nav>

            <section aria-labelledby="footer-contact">
                <h3 id="footer-contact">Contact Us</h3>
                <address>
                    Email: <a href="mailto:hello@example.com">hello@example.com</a><br>
                        Twitter: <a href="https://twitter.com/webdevblog">@webdevblog</a>
                </address>
            </section>
        </div>

        <div class="footer-bottom">
            <p>
                <small>
                    &copy; <time datetime="2025">2025</time> Web Dev Blog. All rights reserved.
                </small>
            </p>
        </div>
    </footer>
</body>
</html>`;

    const outlineExampleCode = `Page: Mastering Semantic HTML | Web Development Blog

 Header (Site Banner)
    Site Branding
    Navigation: Main
    Search Form

 Main Content
    Navigation: Breadcrumb
    Article: Mastering Semantic HTML
       Header
          H1: Article Title
          Author: Jane Doe
          Publish Date: Jan 15, 2025
       Content
          Section: Introduction (H2)
          Section: What is Semantic HTML? (H2)
          Section: Why it Matters (H2)
             H3: Accessibility Benefits
             H3: SEO Advantages
             H3: Code Maintainability
          Section: Practical Example (H2)
          Section: Key Takeaways (H2)
       Footer
           Tags
           Share Buttons
    Section: Related Articles (H2)
    Section: Comments (H2)
        Form: Add Comment

 Aside (Complementary)
    Section: About the Author
    Section: Newsletter
    Section: Popular Articles

 Footer (Site Info)
     Section: About
     Navigation: Footer
     Section: Contact`;

    const schemaExampleCode = `<article itemscope itemtype="https://schema.org/BlogPosting">
    <header>
        <h1 itemprop="headline">Article Title</h1>
        
        <img itemprop="image" 
             src="cover.jpg" 
             alt="Cover image">
        
        <address itemprop="author" itemscope itemtype="https://schema.org/Person">
            By <span itemprop="name">Jane Doe</span>
        </address>
        
        <time itemprop="datePublished" datetime="2025-01-15">
            January 15, 2025
        </time>
        
        <div itemprop="publisher" itemscope itemtype="https://schema.org/Organization">
            <meta itemprop="name" content="Web Dev Blog">
            <link itemprop="logo" href="logo.png">
        </div>
    </header>
    
    <div itemprop="articleBody">
        <!-- Article content --&gt;
    </div>
</article>`;

    const ariaNavCode = `<nav aria-label="Main navigation">...</nav>
<nav aria-label="Footer navigation">...</nav>
<button aria-label="Close dialog">X</button>`;

    const ariaLabelledByCode = `<section aria-labelledby="comments-heading">
    <h2 id="comments-heading">Comments</h2>
    <!-- Comment content --&gt;
</section>`;

    const ariaCurrentCode = `<nav>
    <a href="/" aria-current="page">Home</a>
    <a href="/about">About</a>
</nav>`;

    const ariaRoleCode = `<header role="banner">...</header>
<main role="main">...</main>
<footer role="contentinfo">...</footer>
<aside role="complementary">...</aside>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Semantic HTML5</div>
                <h1>Complete Semantic Page Structure</h1>
                <p className="hero-lead">
                    Master the art of semantic HTML by building a complete, production-ready page structure.
                    Learn how all semantic elements work together to create accessible, SEO-friendly websites.
                </p>
            </div>

            <div className="content-section">
                <h2>What is Semantic Mastery?</h2>
                <p>
                    Semantic mastery means understanding not just individual elements, but how they work together
                    to create meaningful, well-structured documents that serve users, search engines, and assistive technologies.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Purpose-Driven</h3>
                        <p>Every element chosen for its semantic meaning, not just visual appearance</p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Hierarchical</h3>
                        <p>Clear document outline with proper heading levels and nesting</p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>Accessible</h3>
                        <p>Screen readers can navigate efficiently using landmarks and headings</p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>SEO-Optimized</h3>
                        <p>Search engines understand content hierarchy and relevance</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The Complete Semantic Toolkit</h2>
                <p>
                    Let's review all major semantic elements and their proper use:
                </p>

                <div className="element-reference">
                    <div className="category-section">
                        <h3>Document Structure</h3>
                        <div className="element-grid">
                            <div className="element-item">
                                <code>&lt;html&gt;</code>
                                <p>Root element</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;head&gt;</code>
                                <p>Metadata container</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;body&gt;</code>
                                <p>Visible content</p>
                            </div>
                        </div>
                    </div>

                    <div className="category-section">
                        <h3>Page Landmarks</h3>
                        <div className="element-grid">
                            <div className="element-item">
                                <code>&lt;header&gt;</code>
                                <p>Introductory content</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;nav&gt;</code>
                                <p>Navigation links</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;main&gt;</code>
                                <p>Primary content (once per page)</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;aside&gt;</code>
                                <p>Complementary content</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;footer&gt;</code>
                                <p>Footer information</p>
                            </div>
                        </div>
                    </div>

                    <div className="category-section">
                        <h3>Content Sections</h3>
                        <div className="element-grid">
                            <div className="element-item">
                                <code>&lt;article&gt;</code>
                                <p>Self-contained content</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;section&gt;</code>
                                <p>Thematic grouping</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;figure&gt;</code>
                                <p>Self-contained media</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;figcaption&gt;</code>
                                <p>Figure caption</p>
                            </div>
                        </div>
                    </div>

                    <div className="category-section">
                        <h3>Text Semantics</h3>
                        <div className="element-grid">
                            <div className="element-item">
                                <code>&lt;h1&gt;-&lt;h6&gt;</code>
                                <p>Headings (hierarchy)</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;p&gt;</code>
                                <p>Paragraphs</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;blockquote&gt;</code>
                                <p>Extended quotations</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;cite&gt;</code>
                                <p>Citation/reference</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;time&gt;</code>
                                <p>Dates and times</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;address&gt;</code>
                                <p>Contact information</p>
                            </div>
                        </div>
                    </div>

                    <div className="category-section">
                        <h3>Emphasis & Importance</h3>
                        <div className="element-grid">
                            <div className="element-item">
                                <code>&lt;strong&gt;</code>
                                <p>Strong importance</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;em&gt;</code>
                                <p>Emphasized text</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;mark&gt;</code>
                                <p>Highlighted/marked</p>
                            </div>
                            <div className="element-item">
                                <code>&lt;small&gt;</code>
                                <p>Fine print</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Building a Complete Semantic Page</h2>
                <p>
                    Let's build a full blog article page using proper semantic structure:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Complete Semantic Blog Article Page</span>
                    </div>
                    <CodeBlock language="html" code={completeSemanticPageCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Document Outline Visualization</h2>
                <p>
                    Here's how the above structure appears to screen readers and search engines:
                </p>

                <div className="outline-box">
                    <CodeBlock language="html" code={outlineExampleCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Schema.org Microdata</h2>
                <p>
                    Enhance SEO by adding structured data with Schema.org vocabulary:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Article with Schema.org Microdata</span>
                    </div>
                    <CodeBlock language="html" code={schemaExampleCode} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Result:</strong> Rich snippets in search results with author photo, publish date,
                        and rating stars (if reviews included).
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Accessibility ARIA Enhancements</h2>
                <p>
                    ARIA (Accessible Rich Internet Applications) attributes enhance semantic HTML:
                </p>

                <div className="aria-examples">
                    <h3>Common ARIA Attributes:</h3>

                    <div className="aria-card">
                        <h4><code>aria-label</code></h4>
                        <p>Provides accessible name when visible label doesn't exist</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={ariaNavCode} />
                        </div>
                    </div>

                    <div className="aria-card">
                        <h4><code>aria-labelledby</code></h4>
                        <p>References another element as the label</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={ariaLabelledByCode} />
                        </div>
                    </div>

                    <div className="aria-card">
                        <h4><code>aria-current</code></h4>
                        <p>Indicates current item in navigation</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={ariaCurrentCode} />
                        </div>
                    </div>

                    <div className="aria-card">
                        <h4><code>role</code></h4>
                        <p>Explicit role when semantic element isn't available</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={ariaRoleCode} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Your Semantic Structure</h2>
                <p>
                    Verify your semantic HTML works correctly:
                </p>

                <div className="testing-checklist">
                    <h3>Testing Checklist:</h3>
                    <ol>
                        <li>
                            <strong>HTML Validator:</strong> Use <a href="https://validator.w3.org/" target="_blank" rel="noopener">W3C Markup Validator</a>
                        </li>
                        <li>
                            <strong>Document Outline:</strong> Use browser extension like "HeadingsMap" to verify hierarchy
                        </li>
                        <li>
                            <strong>Screen Reader:</strong> Test with NVDA (Windows) or VoiceOver (Mac)
                        </li>
                        <li>
                            <strong>Lighthouse:</strong> Run accessibility audit in Chrome DevTools
                        </li>
                        <li>
                            <strong>axe DevTools:</strong> Browser extension for comprehensive accessibility testing
                        </li>
                        <li>
                            <strong>Keyboard Navigation:</strong> Navigate entire page using only Tab/Shift+Tab
                        </li>
                    </ol>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use semantic elements consistently throughout your site</li>
                            <li>Maintain proper heading hierarchy (H1  H2  H3)</li>
                            <li>Include exactly one <code>&lt;main&gt;</code> per page</li>
                            <li>Add ARIA labels for multiple navigation elements</li>
                            <li>Provide skip links for keyboard users</li>
                            <li>Test with actual screen readers</li>
                            <li>Add Schema.org microdata for rich snippets</li>
                            <li>Validate HTML regularly</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use <code>&lt;div&gt;</code> when semantic element exists</li>
                            <li>Skip heading levels (H1  H3)</li>
                            <li>Have multiple <code>&lt;h1&gt;</code> elements</li>
                            <li>Nest <code>&lt;main&gt;</code> inside other sectioning elements</li>
                            <li>Use semantic elements purely for styling</li>
                            <li>Forget to test accessibility</li>
                            <li>Mix semantic and non-semantic approaches</li>
                            <li>Ignore document outline structure</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Final Thoughts</h2>
                <p>
                    Semantic HTML is not just about following rulesit's about creating a better web for everyone.
                    When you use semantic elements correctly:
                </p>

                <ul>
                    <li>Your content is <strong>more discoverable</strong> in search engines</li>
                    <li>Your site is <strong>accessible</strong> to people using assistive technologies</li>
                    <li>Your code is <strong>easier to maintain</strong> and understand</li>
                    <li>Your pages <strong>load faster</strong> with less markup</li>
                    <li>Your website <strong>future-proof</strong> as standards evolve</li>
                </ul>

                <div className="quote-box">
                    <blockquote>
                        <p>
                            "The power of the Web is in its universality. Access by everyone regardless of disability is an essential aspect."
                        </p>
                        <footer> Tim Berners-Lee, W3C Director and inventor of the World Wide Web</footer>
                    </blockquote>
                </div>
            </div>

        </div>
    );
};

export default HTMLSemanticMastery;


